import {
	useCallback, useEffect, useRef, useState, ChangeEvent
} from 'react';
import { useRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import {
	Modal, Text, Label, Caption, Loader, Spacer, StatusChip, Tooltip
} from '@components';
import { GlobalSearchOpen } from '@state';
import { useGetShipper, useGetCarrier, useEOSFetchShipments } from '@api';
import { useTransportShipmentNavigation, useLocalStorage } from '@hooks';
import {
	getStaticAdminShipmentStatusChipConfig, isPickupOrDropoffDelayed, ADMIN_DELAYED_HOVER_TEXT,
	getPickupDate, getDropoffDate, dateFromISOint, formatDate, formatTimeFromString, timeAgo
} from '@utils';
import {
	TTransportShipment, ShipperSearch, CarrierSearch, CHIP_SIZE, TOOLTIP_POSITION, STATIC_ADMIN_SHIPMENT_STATUS
} from '@types';
import styles from './GlobalSearch.module.scss';

const MIN_CHARS = 2;
const RESULT_LIMIT = 5;
const HISTORY_KEY = '@Ofload/GlobalSearchHistory';
const HISTORY_MAX = 25;
const PLACEHOLDER_TERMS = ['reference', 'order', 'shipper', 'carrier', 'superdock'];

type THistoryItem = { term: string; timestamp: number };

/* Typesense fields requested from the transport-shipment endpoint. `fields` is
   required by the API; extend this list as result rows surface more data. */
const TRANSPORT_SEARCH_FIELDS = [
	'id',
	'external_id',
	'reference',
	'shipment_status.for_admin',
	'job_list_id',
	'actual_pickup_at',
	'actual_dropoff_at',
	'original_pickup_on',
	'original_pickup_from',
	'original_pickup_to',
	'original_dropoff_on',
	'original_dropoff_from',
	'original_dropoff_to',
	'revised_pickup',
	'revised_dropoff',
	'manifest.id',
	'master_shipment.shipper.company.business_name',
	'manifest.master_manifest.carrier',
	'manifest.master_manifest.super_dock',
	'cargos'
];

export const GlobalSearch = () => {
	const [open, setOpen] = useRecoilState(GlobalSearchOpen);
	const [searchTerm, setSearchTerm] = useState('');
	const [transportRows, setTransportRows] = useState<TTransportShipment[]>([]);
	const [transportShowAll, setTransportShowAll] = useState(false);
	const [superdockShowAll, setSuperdockShowAll] = useState(false);
	const [shipperShowAll, setShipperShowAll] = useState(false);
	const [carrierShowAll, setCarrierShowAll] = useState(false);
	const [history, setHistory] = useLocalStorage<THistoryItem[]>(HISTORY_KEY, []);
	const [historyShowAll, setHistoryShowAll] = useState(false);
	const [placeholderIndex, setPlaceholderIndex] = useState(0);
	const [placeholderVisible, setPlaceholderVisible] = useState(true);
	const inputRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();
	const { goToShipment } = useTransportShipmentNavigation();
	const { callGetShippers, shippers, loadingShippers } = useGetShipper();
	const { callGetCarriers, carriers, loadingCarriers } = useGetCarrier();
	const { fetchShipments, isLoadingShipments } = useEOSFetchShipments();

	const shipperResults = shippers?.searchResult?.shipper ?? [];
	const carrierResults = carriers?.searchResult?.carriers ?? [];
	const superdockRefs = Array.from(new Set(
		transportRows
			.map((row) => row.manifest?.master_manifest?.super_dock?.reference)
			.filter((ref): ref is string => Boolean(ref))
	));
	const isLoading = loadingShippers || loadingCarriers || isLoadingShipments;
	const hasQuery = searchTerm.trim().length >= MIN_CHARS;
	const hasResults = transportRows.length > 0 || shipperResults.length > 0 || carrierResults.length > 0;
	const showHistory = !hasQuery && history.length > 0;

	const runSearch = useCallback(
		debounce((value: string) => {
			const q = value.trim();
			if (q.length < MIN_CHARS) {
				setTransportRows([]);
				return;
			}
			callGetShippers('shipper', q);
			callGetCarriers('carrier', q);
			fetchShipments({
				payload: {
					q,
					page: '1',
					per_page: '15',
					fields: TRANSPORT_SEARCH_FIELDS,
					sort_by: [{ field: 'created_at', direction: 'desc' }]
				},
				avoidCache: true
			})
				.then((res) => setTransportRows(res?.data?.hits?.map((hit) => hit.document) ?? []))
				.catch(() => setTransportRows([]));
			setHistory((prev) => {
				const deduped = prev.filter((item) => item.term.toLowerCase() !== q.toLowerCase());
				return [{ term: q, timestamp: Date.now() }, ...deduped].slice(0, HISTORY_MAX);
			});
		}, 400),
		[]
	);

	const close = () => setOpen(false);

	const collapseGroups = () => {
		setTransportShowAll(false);
		setSuperdockShowAll(false);
		setShipperShowAll(false);
		setCarrierShowAll(false);
	};

	const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		collapseGroups();
		runSearch(e.target.value);
	};

	const clearSearch = () => {
		setSearchTerm('');
		setTransportRows([]);
		collapseGroups();
		runSearch.cancel();
	};

	const onTransportSelect = (row: TTransportShipment) => {
		goToShipment(row);
		close();
	};

	const onShipperSelect = (shipper: ShipperSearch) => {
		navigate(`/shipper/orders?sid=${shipper.shipper_id}`);
		close();
	};

	const onCarrierSelect = (carrier: CarrierSearch) => {
		navigate(`/carrier/your-loads?cid=${carrier.carrier_id}`);
		close();
	};

	const onSuperdockSelect = (reference: string) => {
		navigate(`/transport/superdock?filter[search]=${reference}`);
		close();
	};

	const onHistorySelect = (term: string) => {
		setSearchTerm(term);
		collapseGroups();
		setHistoryShowAll(false);
		runSearch(term);
	};

	const transportRoute = (row: TTransportShipment) => {
		const shipperName = row.master_shipment?.shipper?.company?.business_name;
		const carrierName = row.manifest?.master_manifest?.carrier?.company?.business_name;
		const parts = [shipperName, carrierName].filter(Boolean);
		return parts.length ? parts.join(' → ') : null;
	};

	const formatStop = (iso?: string | null, fromTime?: string, toTime?: string) => {
		if (!iso) return null;
		const date = new Date(iso);
		if (isNaN(date.getTime())) return null;
		const datePart = formatDate(date, 'US_DATE_WITH_SHORT_MONTH_NAME');
		const timePart = fromTime && toTime ? `${formatTimeFromString(fromTime)} - ${formatTimeFromString(toTime)}` : '';
		return timePart ? `${datePart}, ${timePart}` : datePart;
	};

	const transportPickup = (row: TTransportShipment) => {
		if (row.revised_pickup?.request_arrival_date) {
			return formatStop(row.revised_pickup.request_arrival_date, row.revised_pickup.request_arrival_start_time, row.revised_pickup.request_arrival_end_time);
		}
		return formatStop(dateFromISOint(row.original_pickup_on), row.original_pickup_from, row.original_pickup_to);
	};

	const transportDropoff = (row: TTransportShipment) => {
		if (row.revised_dropoff?.request_arrival_date) {
			return formatStop(row.revised_dropoff.request_arrival_date, row.revised_dropoff.request_arrival_start_time, row.revised_dropoff.request_arrival_end_time);
		}
		return formatStop(dateFromISOint(row.original_dropoff_on), row.original_dropoff_from, row.original_dropoff_to);
	};

	const transportStatus = (row: TTransportShipment) => {
		const status = row.shipment_status?.for_admin;
		const staticStatus = getStaticAdminShipmentStatusChipConfig(status ?? '');
		if (!staticStatus) return null;

		const isDelayable = status === STATIC_ADMIN_SHIPMENT_STATUS.ALLOCATED || status === STATIC_ADMIN_SHIPMENT_STATUS.ONROAD;
		const label = isDelayable && isPickupOrDropoffDelayed({
			now: new Date(),
			pickupHasOccurred: !!row.actual_pickup_at,
			scheduledPickupAt: getPickupDate(row),
			dropoffHasOccurred: !!row.actual_dropoff_at,
			scheduledDropoffAt: getDropoffDate(row)
		}) ? ADMIN_DELAYED_HOVER_TEXT : staticStatus.label;
		return (
			<Tooltip text={label} position={TOOLTIP_POSITION.TOP}>
				<StatusChip type={staticStatus.type} size={CHIP_SIZE.SMALL} text={staticStatus.short} showIcon CustomIcon={staticStatus.icon} />
			</Tooltip>
		);
	};

	const showMoreButton = (total: number, showAll: boolean, onShowAll: () => void) => {
		if (total <= RESULT_LIMIT || showAll) return null;
		return (
			<button className={styles.showMore} onClick={onShowAll}>
				<Text color={'var(--brand-orange-default)'}>Show {total - RESULT_LIMIT} more</Text>
			</button>
		);
	};

	useEffect(() => {
		if (open) {
			const timer = setTimeout(() => inputRef.current?.focus(), 0);
			return () => clearTimeout(timer);
		}
		setSearchTerm('');
		setTransportRows([]);
		collapseGroups();
		setHistoryShowAll(false);
		runSearch.cancel();
	}, [open]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
				e.preventDefault();
				setOpen((prev) => !prev);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			runSearch.cancel();
		};
	}, []);

	useEffect(() => {
		if (!open) {
			setPlaceholderIndex(0);
			setPlaceholderVisible(true);
			return;
		}
		let swap: ReturnType<typeof setTimeout> | undefined;
		const cycle = setInterval(() => {
			setPlaceholderVisible(false);
			swap = setTimeout(() => {
				setPlaceholderIndex((i) => (i + 1) % PLACEHOLDER_TERMS.length);
				setPlaceholderVisible(true);
			}, 400);
		}, 2000);
		return () => {
			clearInterval(cycle);
			clearTimeout(swap);
		};
	}, [open]);

	return (
		<Modal
			open={open}
			toggleOpen={setOpen}
			showCloseButton={false}
			closeOnEscape
			closeOnClickOutside
			blurBackdrop
			glow
			padding={0}
		>
			<div data-testid={'@Ofload-GlobalSearch'} className={styles.palette}>
				<div className={styles.searchRow}>
					<SearchIcon className={styles.searchIcon} />
					<div className={styles.inputWrap}>
						<input
							ref={inputRef}
							className={styles.input}
							name={'global-search'}
							aria-label={'Search'}
							autoComplete={'off'}
							value={searchTerm}
							onChange={onSearchChange}
						/>
						{!searchTerm && (
							<div aria-hidden={true} className={styles.placeholder}>
								Search&nbsp;
								<span className={`${styles.keyword} ${placeholderVisible ? '' : styles.keywordHidden}`}>
									{PLACEHOLDER_TERMS[placeholderIndex]}
								</span>
							</div>
						)}
					</div>
					{searchTerm && (
						<button className={styles.clearBtn} onClick={clearSearch} aria-label={'Clear search'}>
							<ClearIcon />
						</button>
					)}
				</div>
				{(hasQuery || showHistory) && <Spacer size={8} divider />}
				{hasQuery && (
					<div className={styles.results}>
						{isLoading && !hasResults && (
							<div className={styles.state}><Loader /></div>
						)}
						{!isLoading && !hasResults && (
							<div className={styles.state}><Text>No results found</Text></div>
						)}
						{transportRows.length > 0 && (
							<div className={styles.group}>
								<div className={styles.groupHeader}><Label text={'Transport'} /></div>
								{(transportShowAll ? transportRows : transportRows.slice(0, RESULT_LIMIT)).map((row) => {
									const route = transportRoute(row);
									const pickup = transportPickup(row);
									const dropoff = transportDropoff(row);
									return (
										<button key={row.id} className={styles.resultRow} onClick={() => onTransportSelect(row)}>
											<div className={styles.resultText}>
												<div className={styles.resultTitle}>
													<Text>{row.reference}</Text>
													{transportStatus(row)}
												</div>
												{route && <Caption color={'var(--txt-disabled)'}>{route}</Caption>}
												{(pickup || dropoff) && (
													<Caption color={'var(--txt-disabled)'}>Pickup: {pickup ?? '—'} · Dropoff: {dropoff ?? '—'}</Caption>
												)}
											</div>
											<ChevronRightIcon className={styles.chevron} />
										</button>
									);
								})}
								{showMoreButton(transportRows.length, transportShowAll, () => setTransportShowAll(true))}
							</div>
						)}
						{superdockRefs.length > 0 && (
							<div className={styles.group}>
								<div className={styles.groupHeader}><Label text={'Superdocks'} /></div>
								{(superdockShowAll ? superdockRefs : superdockRefs.slice(0, RESULT_LIMIT)).map((ref) => (
									<button key={ref} className={styles.resultRow} onClick={() => onSuperdockSelect(ref)}>
										<div className={styles.resultText}>
											<Text>{ref}</Text>
										</div>
										<ChevronRightIcon className={styles.chevron} />
									</button>
								))}
								{showMoreButton(superdockRefs.length, superdockShowAll, () => setSuperdockShowAll(true))}
							</div>
						)}
						{shipperResults.length > 0 && (
							<div className={styles.group}>
								<div className={styles.groupHeader}><Label text={'Shippers'} /></div>
								{(shipperShowAll ? shipperResults : shipperResults.slice(0, RESULT_LIMIT)).map((shipper, i) => (
									<button key={shipper.shipper_id ?? i} className={styles.resultRow} onClick={() => onShipperSelect(shipper)}>
										<div className={styles.resultText}>
											<Text>{shipper.trade_name || shipper.company_name}</Text>
										</div>
										<ChevronRightIcon className={styles.chevron} />
									</button>
								))}
								{showMoreButton(shipperResults.length, shipperShowAll, () => setShipperShowAll(true))}
							</div>
						)}
						{carrierResults.length > 0 && (
							<div className={styles.group}>
								<div className={styles.groupHeader}><Label text={'Carriers'} /></div>
								{(carrierShowAll ? carrierResults : carrierResults.slice(0, RESULT_LIMIT)).map((carrier, i) => (
									<button key={carrier.carrier_id ?? i} className={styles.resultRow} onClick={() => onCarrierSelect(carrier)}>
										<div className={styles.resultText}>
											<Text>{carrier.trade_name || carrier.company_name}</Text>
										</div>
										<ChevronRightIcon className={styles.chevron} />
									</button>
								))}
								{showMoreButton(carrierResults.length, carrierShowAll, () => setCarrierShowAll(true))}
							</div>
						)}
					</div>
				)}
				{showHistory && (
					<div className={styles.results}>
						<div className={styles.group}>
							<div className={styles.groupHeader}><Label text={'Recent searches'} /></div>
							{(historyShowAll ? history : history.slice(0, RESULT_LIMIT)).map((item) => (
								<button key={item.term} className={styles.resultRow} onClick={() => onHistorySelect(item.term)}>
									<div className={styles.resultText}>
										<Text>{item.term}</Text>
									</div>
									<Caption color={'var(--txt-disabled)'}>{timeAgo(item.timestamp)}</Caption>
								</button>
							))}
							{showMoreButton(history.length, historyShowAll, () => setHistoryShowAll(true))}
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};
